import { EnableDisableStatus } from '@Policy/utils/types';
import { AllAggressivity } from './utils';

export enum ConditionDataType {
  Int = 'int',
  String = 'string',
  ListString = 'listString',
}

export enum ConditionOperator {
  IntGTE = 'IntGTE',
  IntLTE = 'IntLTE',
  IntNE = 'IntNE',
  IntEQ = 'IntEQ',
  IntLTEGTE = 'IntLTEGTE',
  StringInList = 'StringInList',
  StringContains = 'StringContains',
  StringStartsWith = 'StringStartsWith',
  StringEndsWith = 'StringEndsWith',
  StringInListWithWildcard = 'StringInListWithWildcard',
  StringNotInListWithWildcard = 'StringNotInListWithWildcard',
}

type ConditionDefaultValue = number | string | string[];

interface ConditionFormat<T extends ConditionDefaultValue> {
  defaultValue?: T;
}

export interface ConditionIntFormat extends ConditionFormat<number> {
  minValue: number;
  maxValue: number;
}

export interface ConditionListStringFormat extends ConditionFormat<string[]> {
  maxEntries: number;
  eachEntryMinLength: number;
  eachEntryMaxLength: number;
}

export interface ConditionStringFormat extends ConditionFormat<string> {
  minLength: number;
  maxLength: number;
}

export interface ConditionEnumFormat extends ConditionFormat<number> {
  enums: Record<string, number>;
}

export function isConditionIntFormat(
  format: ConditionFormat<ConditionDefaultValue>,
  condDataType: ConditionDataType
): format is ConditionIntFormat {
  return (
    condDataType === ConditionDataType.Int &&
    (format as ConditionEnumFormat).enums === undefined
  );
}

export function isConditionStringFormat(
  format: ConditionFormat<ConditionDefaultValue>,
  condDataType: ConditionDataType
): format is ConditionStringFormat {
  return (
    condDataType === ConditionDataType.String &&
    (format as ConditionStringFormat).maxLength !== undefined
  );
}

export function isConditionListStringFormat(
  format: ConditionFormat<ConditionDefaultValue>,
  condDataType: ConditionDataType
): format is ConditionListStringFormat {
  return (
    condDataType === ConditionDataType.ListString &&
    (format as ConditionListStringFormat).maxEntries !== undefined
  );
}

export function isConditionEnumFormat(
  format: ConditionFormat<ConditionDefaultValue>,
  condDataType: ConditionDataType
): format is ConditionEnumFormat {
  return (
    condDataType === ConditionDataType.Int &&
    (format as ConditionEnumFormat).enums !== undefined
  );
}

export enum ConditionLogicOperator {
  AND = 'AND',
  OR = 'OR',
}

export interface ConditionDefinition {
  raiCondId: string;
  raiCondName: string;
  raiCondDataType: ConditionDataType;
  raiCondValidOperators: ConditionOperator[];
  raiCondFormat:
    | ConditionIntFormat
    | ConditionStringFormat
    | ConditionListStringFormat
    | ConditionEnumFormat;
  raiCondUnit: string; // empty string if no unit
  raiCondMarker: string; // empty string if no marker
}

export interface CorrelatedExpressionShallow {
  raiExpId: string;
  raiExpBuiltIn: RuleType;
  raiExpName: string;
  raiExpDesc: string;
  raiExpMarkers: string;
}

export interface CorrelatedExpression {
  raiExpId: string;
  raiExpBuiltIn: RuleType;
  raiExpName: string;
  raiExpDesc: string;
  raiExpContent: CorrelatedExpressionContent;
  raiExpMarkers?: string;
}

export interface CorrelatedExpressionCondition {
  id: string;
  name: string;
  operator: ConditionOperator;
  marker: string; // empty string if no marker
  value?: number;
  valueString?: string;
  valueStrings?: string[];
  greaterOrEqualValue?: number;
  lessOrEqualValue?: number;
}

export interface CorrelatedExpressionContent {
  conditionLogic: ConditionLogicOperator;
  conditions: CorrelatedExpressionCondition[];
}

export interface CorrelatedRuleShallow {
  raiRuleId: string;
  raiRuleBuiltIn: RuleType;
  raiRuleName: string;
  raiRuleDesc: string;
  raiAggressiveLevel: CorrelatedRuleAggressivity;
  raiThreatType?: string;
  raiSocialGraphEnabled: EnableDisableStatus;
  raiEmailLoopEnabled?: boolean;
  raiExpressionList: { raiExpId: string; raiExpName: string }[];
}

export interface CorrelatedRule {
  raiRuleId: string;
  raiRuleBuiltIn: RuleType;
  raiRuleName: string;
  raiRuleDesc: string;
  raiRuleContent: CorrelatedRuleContent[];
}

export interface CorrelatedRuleContent {
  statement: CorrelatedStatementExpression[];
}

export interface CorrelatedStatementExpression {
  raiExpId: string;
  raiExpBuiltIn: RuleType;
}

export enum CorrelatedRuleAggressivity {
  Moderate = 0,
  Aggressive = 1,
  SuperAggressive = 2,
}

export type AggressivityFilter =
  | CorrelatedRuleAggressivity
  | typeof AllAggressivity;

export type ThreatTypeFilter = 'All' | string;

export enum RuleType {
  UserDefined = 0,
  PreDefined = 1,
}

export enum RuleTypeFilter {
  All = 'All',
  UserDefined = 'UserDefined',
  PreDefined = 'PreDefined',
}
